/**
 * Remembering which provider was used on this device.
 *
 * Prevents the duplicate-account failure at its source: sign up with Google,
 * come back a month later, tap Discord, land in an empty account. Account
 * linking cleans that up *afterwards*; this stops it happening.
 *
 * Device-scoped by design — the question is "which button did I press on this
 * machine", which is exactly what someone is trying to remember.
 *
 * Pure and storage-agnostic so the rules are testable without a browser.
 */

export type ProviderId = "discord" | "google";

const KNOWN: readonly ProviderId[] = ["discord", "google"];

export function isKnownProvider(value: unknown): value is ProviderId {
  return typeof value === "string" && (KNOWN as readonly string[]).includes(value);
}

/**
 * Remembered provider first, original order otherwise.
 *
 * Only reorders — never hides the alternative. Someone whose Discord account is
 * gone must still be able to reach Google without hunting for it.
 */
export function orderProviders(
  available: readonly ProviderId[],
  lastUsed: ProviderId | null,
): ProviderId[] {
  if (!lastUsed || !available.includes(lastUsed)) return [...available];
  return [lastUsed, ...available.filter((p) => p !== lastUsed)];
}

const LABEL: Record<ProviderId, string> = {
  discord: "Discord",
  google: "Google",
};

/**
 * The hint, or null when there is nothing useful to say.
 *
 * Suppressed when the remembered provider is no longer offered — pointing
 * someone at a button that is not there is worse than staying quiet. Also
 * suppressed when it is the only option, where it would be noise.
 */
export function hintFor(
  lastUsed: ProviderId | null,
  available: readonly ProviderId[],
): string | null {
  if (!lastUsed || !available.includes(lastUsed) || available.length < 2) return null;
  return `You used ${LABEL[lastUsed]} last time`;
}

/**
 * Whether a provider attempt should be recorded as "last used".
 *
 * Only once a session actually exists. Recording at initiation would remember a
 * provider the user cancelled on, and then confidently recommend it forever.
 */
export function shouldPromote(pending: string | null, signedIn: boolean): pending is ProviderId {
  return signedIn && isKnownProvider(pending);
}
